import { toast } from 'react-toastify'
import { getErrorMessage } from '../../helpers'
import type {
  BaseApiResponse,
  DateFilterQueryProps,
  QueryProps,
} from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  AddCreatorPayload,
  AddCuratedProductPayload,
  CreateVendorFee,
  CreatorChannelBreakdownResponse,
  CreatorCuratedProductsResponse,
  CreatorCurationResponse,
  CreatorSection,
  CreatorSettlementConfiguration,
  InviteVendorPayload,
  InviteVendorResponse,
  ResendInviteResponse,
  SetVendorDeliveryRegionsPayload,
  SingleVendorResponse,
  SingleVendorsMetricsResponse,
  SingleVendorsSettlement,
  UpdateCreatorDetailsPayload,
  UpdateCreatorOrderResponse,
  UpdateCreatorSettlementConfiguration,
  UpdateCreatorSplit,
  UpdateVendorFee,
  UpdateVendorInventoryModePayload,
  UpdateVendorSettlementAccountPayload,
  VendorInvitesResponse,
  VendorsMetricsResponse,
  VendorsQuery,
  VendorsResponse,
} from './vendors.types'
import {
  AddFulfillmentCenterCreatorsPayload,
  CreateFulfilmentCenter,
  CreateFulfilmentCenterResponse,
  FulfilmentCentreResponse,
  GetFulfilmentCentre,
  RemoveFulfillmentCenterCreatorsPayload,
} from '../fulfilment-centres/fulfilmentCentres.type'

export const vendorsApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAllVendors: builder.query<VendorsResponse, VendorsQuery>({
      query: (params) => {
        return {
          url: `v1/vendors`,
          method: 'GET',
          params: {
            ...params,
            isAdmin: true,
          },
        }
      },
      providesTags: [{ type: 'VENDOR', id: 'LIST' }],
    }),
    getAllVendorsMetrics: builder.query<
      VendorsMetricsResponse,
      {
        vendorId?: string
        fulfillmentCentreId?: string
      }
    >({
      query: (params) => {
        return {
          url: `v1/vendors/metrics`,
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['VENDOR'],
    }),
    getSingleVendor: builder.query<SingleVendorResponse, { id: string }>({
      query: (params) => {
        return {
          url: `v1/vendors/${params.id}`,
          method: 'GET',
        }
      },
      providesTags: (_result, _error, arg) => [{ type: 'VENDOR', id: arg.id }],
    }),
    getSingleVendorsMetrics: builder.query<
      SingleVendorsMetricsResponse,
      { id: string; fulfillmentCentreId?: string } & QueryProps
    >({
      query: ({ id, ...params }) => {
        return {
          url: `v1/vendors/metrics/${id}`,
          method: 'GET',
          params: params,
        }
      },
      providesTags: (_result, _error, arg) => [{ type: 'VENDOR', id: arg.id }],
    }),
    getSingleVendorsSettlements: builder.query<
      SingleVendorsSettlement,
      { id: string; status?: string } & QueryProps
    >({
      query: ({ id, ...params }) => {
        return {
          url: `v1/vendors/settlement/${id}`,
          method: 'GET',
          params: { ...params },
        }
      },
      providesTags: [{ type: 'SETTLEMENT', id: 'LIST' }],
    }),
    getAllVendorInvites: builder.query<VendorInvitesResponse, QueryProps>({
      query: (params) => {
        return {
          url: `v1/vendors/invitations`,
          method: 'GET',
          params: {
            page: params.page ?? 1,
            limit: params.limit ?? 20,
          },
        }
      },
      providesTags: [{ type: 'VENDOR_INVITE', id: 'LIST' }],
    }),
    inviteVendor: builder.mutation<InviteVendorResponse, InviteVendorPayload>({
      query: (payload) => ({
        url: `v1/vendors/invitations`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'VENDOR_INVITE', id: 'LIST' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Creator invited successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    reInviteVendor: builder.mutation<ResendInviteResponse, { id: string }>({
      query: (payload) => ({
        url: `v1/vendors/invitations/${payload.id}/resend`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VENDOR_INVITE', id: arg.id },
        { type: 'VENDOR_INVITE', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled
          toast.success(resp.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateVendorDetails: builder.mutation<
      BaseApiResponse,
      { id: string; data: UpdateCreatorDetailsPayload }
    >({
      query: (payload) => ({
        url: `v1/vendors/${payload.id}`,
        method: 'PATCH',
        body: payload.data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VENDOR', id: arg.id },
        { type: 'VENDOR', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateVendorInventoryMode: builder.mutation<
      BaseApiResponse,
      UpdateVendorInventoryModePayload
    >({
      query: ({ id, ...body }) => ({
        url: `v1/vendors/${id}/inventory-mode`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VENDOR', id: arg.id },
        { type: 'VENDOR', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateVendorSettlementAccount: builder.mutation<
      BaseApiResponse,
      { id: string; data: UpdateVendorSettlementAccountPayload }
    >({
      query: (payload) => ({
        url: `v1/vendors/${payload.id}/settlement-account`,
        method: 'PATCH',
        body: payload.data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VENDOR', id: arg.id },
        { type: 'SETTLEMENT_ACCOUNT', id: arg.id },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getFulfilmentCenters: builder.query<FulfilmentCentreResponse, QueryProps>({
      transformResponse: (response: FulfilmentCentreResponse) => ({
        ...response,
        data: {
          ...response.data,
          docs: [...response.data.docs].sort(
            (first, second) =>
              Number(second.role === 'PARENT') -
              Number(first.role === 'PARENT'),
          ),
        },
      }),
      query: (params) => {
        return {
          url: `v1/fulfilment-centres`,
          method: 'GET',
          params: {
            ...params,
          },
        }
      },
      providesTags: [{ type: 'FULFILMENT_CENTRE', id: 'LIST' }],
    }),
    getFulfilmentCenter: builder.query<
      GetFulfilmentCentre,
      { id: string } & QueryProps
    >({
      query: ({ id, ...params }) => {
        return {
          url: `v1/fulfilment-centres/${id}/detail`,
          method: 'GET',
          params: {
            ...params,
          },
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'FULFILMENT_CENTRE', id: arg.id },
      ],
    }),
    editFulfilmentCenter: builder.mutation<
      CreateFulfilmentCenterResponse,
      { id: string } & Partial<CreateFulfilmentCenter>
    >({
      query: ({ id, ...payload }) => ({
        url: `v1/fulfilment-centres/${id}/edit`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FULFILMENT_CENTRE', id: arg.id },
        { type: 'FULFILMENT_CENTRE', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Fulfilment center details updated successfully')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    createFulfilmentCenter: builder.mutation<
      CreateFulfilmentCenterResponse,
      CreateFulfilmentCenter
    >({
      query: (payload) => ({
        url: `v1/fulfilment-centres`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'FULFILMENT_CENTRE', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    addFulfillmentCenterCreators: builder.mutation<
      BaseApiResponse,
      AddFulfillmentCenterCreatorsPayload
    >({
      query: ({ id, ...payload }) => ({
        url: `v1/vendors/${id}/fulfilment-centre`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VENDOR', id: arg.id },
        { type: 'FULFILMENT_CENTRE', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          // toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    removeFulfillmentCenterCreators: builder.mutation<
      BaseApiResponse,
      RemoveFulfillmentCenterCreatorsPayload
    >({
      query: ({ id, ...payload }) => ({
        url: `v1/fulfilment-centres/${id}/remove`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FULFILMENT_CENTRE', id: arg.id },
        { type: 'FULFILMENT_CENTRE', id: 'LIST' },
        { type: 'VENDOR', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          // toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    addCreator: builder.mutation<BaseApiResponse, Partial<AddCreatorPayload>>({
      query: (payload) => ({
        url: `v1/vendors/single-vendor-upload`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'VENDOR', id: 'LIST' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    createFee: builder.mutation<BaseApiResponse, CreateVendorFee>({
      query: (payload) => ({
        url: `v1/fees`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'FEE', id: 'LIST' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateFee: builder.mutation<
      BaseApiResponse,
      { id: string; payload: UpdateVendorFee }
    >({
      query: ({ id, payload }) => ({
        url: `v1/fees/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FEE', id: arg.id },
        { type: 'FEE', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateCreatorSettlement: builder.mutation<
      BaseApiResponse,
      { id: string; payload: UpdateCreatorSettlementConfiguration }
    >({
      query: ({ id, payload }) => ({
        url: `v2/creators/${id}/settlement-configuration`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SETTLEMENT_CONFIG', id: arg.id },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getCreatorSettlementConfiguration: builder.query<
      CreatorSettlementConfiguration,
      { id: string }
    >({
      query: ({ id }) => {
        return {
          url: `v2/creators/${id}/settlement-configuration`,
          method: 'GET',
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'SETTLEMENT_CONFIG', id: arg.id },
      ],
    }),
    getCreatorCuration: builder.query<CreatorCurationResponse, CreatorSection>({
      query: (section) => ({
        url: `v2/creators/curation`,
        method: 'GET',
        params: { section },
      }),
      providesTags: (_result, _error, section) => [
        { type: 'VENDOR_CURATION', id: section },
      ],
    }),
    updateCreatorSectionOrder: builder.mutation<
      UpdateCreatorOrderResponse,
      { section: CreatorSection; creatorIds: string[] }
    >({
      query: (payload) => ({
        url: `v2/creators/curation/order`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VENDOR_CURATION', id: arg.section },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Creator order saved')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateCreatorSplit: builder.mutation<
      BaseApiResponse,
      { id: string; payload: UpdateCreatorSplit }
    >({
      query: ({ id, payload }) => ({
        url: `v1/vendors/${id}/split`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VENDOR', id: arg.id },
        { type: 'SETTLEMENT_CONFIG', id: arg.id },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    setVendorDeliveryRegions: builder.mutation<
      BaseApiResponse,
      SetVendorDeliveryRegionsPayload
    >({
      query: ({ id, regions }) => ({
        url: `v1/vendors/${id}/delivery-regions`,
        method: 'PATCH',
        body: { regions },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VENDOR', id: arg.id },
        { type: 'DELIVERY_REGION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getCreatorChannelBreakdown: builder.query<
      CreatorChannelBreakdownResponse,
      { id: string; fulfilmentCentreId?: string } & DateFilterQueryProps
    >({
      query: ({ id, ...params }) => ({
        url: `v2/creators/${id}/channel-breakdown`,
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'VENDOR', id: arg.id }],
    }),
    getCreatorCuratedProducts: builder.query<
      CreatorCuratedProductsResponse,
      { creatorId: string }
    >({
      query: ({ creatorId }) => ({
        url: `v2/creators/${creatorId}/curated-products`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'CURATED_PRODUCT', id: arg.creatorId },
      ],
    }),
    addCreatorCuratedProduct: builder.mutation<
      BaseApiResponse,
      { creatorId: string } & AddCuratedProductPayload
    >({
      query: ({ creatorId, ...body }) => ({
        url: `v2/creators/${creatorId}/curated-products`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'CURATED_PRODUCT', id: arg.creatorId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteCreatorCuratedProduct: builder.mutation<
      BaseApiResponse,
      { creatorId: string; productId: string }
    >({
      query: ({ creatorId, productId }) => ({
        url: `v2/creators/${creatorId}/curated-products/${productId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'CURATED_PRODUCT', id: arg.creatorId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useGetAllVendorsQuery,
  useLazyGetAllVendorsQuery,
  useGetAllVendorsMetricsQuery,
  useGetSingleVendorQuery,
  useGetSingleVendorsMetricsQuery,
  useInviteVendorMutation,
  useGetAllVendorInvitesQuery,
  useReInviteVendorMutation,
  useGetSingleVendorsSettlementsQuery,
  useUpdateVendorDetailsMutation,
  useUpdateVendorInventoryModeMutation,
  useGetFulfilmentCentersQuery,
  useCreateFulfilmentCenterMutation,
  useUpdateVendorSettlementAccountMutation,
  useAddCreatorMutation,
  useGetFulfilmentCenterQuery,
  useEditFulfilmentCenterMutation,
  useAddFulfillmentCenterCreatorsMutation,
  useRemoveFulfillmentCenterCreatorsMutation,
  useCreateFeeMutation,
  useUpdateFeeMutation,
  useUpdateCreatorSettlementMutation,
  useGetCreatorSettlementConfigurationQuery,
  useGetCreatorCurationQuery,
  useUpdateCreatorSectionOrderMutation,
  useUpdateCreatorSplitMutation,
  useSetVendorDeliveryRegionsMutation,
  useGetCreatorChannelBreakdownQuery,
  useGetCreatorCuratedProductsQuery,
  useAddCreatorCuratedProductMutation,
  useDeleteCreatorCuratedProductMutation,
} = vendorsApiSlice
